import React, { useState, useEffect, useRef, useMemo, useCallback, useReducer } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../../../services/axiosConfig";
import Layout from "../../../components/layout/layout";
import useNotification from "../../../hooks/useNotification";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
// import rrulePlugin from "@fullcalendar/rrule";
import { RRule } from "rrule";
import dayjs from "dayjs";
import { Button, Drawer, List, Pagination, Modal, DatePicker } from "antd";
import { FaSearch, FaArrowCircleRight, FaCalendarAlt, FaClock, FaMapMarkerAlt } from "react-icons/fa";
import { getAuthToken } from "../../../auth/auth";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import "../../../index.css";
import "./eventList.css";

const { RangePicker } = DatePicker;

const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  eventData: [],
  currentPage: 1,
  pageSize: 5,
  showDateRangeModal: false,
  tempFromDate: null,
  tempToDate: null,
  fromDate: null,
  toDate: null,
};

const eventReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setEventData":
      return { ...state, eventData: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize,
      };
    case "setDateRangeModal":
      return { ...state, showDateRangeModal: action.payload };
    case "setTempDateRange":
      return {
        ...state,
        tempFromDate: action.payload.from,
        tempToDate: action.payload.to,
      };
    case "setDateRange":
      return {
        ...state,
        fromDate: action.payload.from,
        toDate: action.payload.to,
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1,
        fromDate: null,
        toDate: null,
        tempFromDate: null,
        tempToDate: null,
        showDateRangeModal: false,
      };
    default:
      return state;
  }
};

function EventList() {
  const navigate = useNavigate();
  const location = useLocation(); // FIX: needed to detect refetch signal from eventDetails
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(eventReducer, initialState);
  const {
    isLoading,
    searchTerm,
    filters,
    eventData,
    currentPage,
    pageSize,
    showDateRangeModal,
    tempFromDate,
    tempToDate,
    fromDate,
    toDate,
  } = state;

  const [calendarRange, setCalendarRange] = useState({ start: null, end: null });
  const [events, setEvents] = useState([]);
  const [popupInfo, setPopupInfo] = useState(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const popupRef = useRef(null);
  const isHoveringPopupRef = useRef(false);
  const calendarWrapperRef = useRef(null);
  const [open, setOpen] = useState(false);

  const openDrawer = () => setOpen(true);
  const closeDrawer = () => setOpen(false);

  const filtersConfig = useMemo(
    () => ({
      eventDate: {
        label: "Event Date",
        options: [
          { value: "today", label: "Today" },
          { value: "thisWeek", label: "This Week" },
          { value: "thisMonth", label: "This Month" },
          { value: "past", label: "Past Events" },
          { value: "dateRange", label: "Custom Date Range" },
        ],
      },
      eventType: {
        label: "Event Type",
        options: [
          { value: "recurring", label: "Recurring" },
          { value: "nonrecurring", label: "One-time" },
        ],
      },
      venue: {
        label: "Venue",
        options: [
          { value: "offline", label: "Offline" },
          { value: "online", label: "Online" },
        ],
      },
    }),
    [],
  );

  const expandRecurringEvents = useCallback(
    (events, monthsBack = 12, monthsAhead = 12) => {
      const expanded = [];
      const now = new Date();

      const startDate = new Date(now);
      startDate.setMonth(now.getMonth() - monthsBack);

      const endDate = new Date(now);
      endDate.setMonth(now.getMonth() + monthsAhead);

      events.forEach((event) => {
        if (event.rrule && event.rrule.dtstart) {
          try {
            const startDateTime = new Date(event.rrule.dtstart);

            const rule = new RRule({
              freq: RRule.WEEKLY,
              dtstart: startDateTime,
              until: endDate,
            });

            const occurrences = rule.between(startDate, endDate, true);

            const durationMs =
              event.duration === "02:00:00"
                ? 2 * 60 * 60 * 1000
                : 60 * 60 * 1000;

            occurrences.forEach((occurrenceDate, index) => {
              // Use dayjs to avoid UTC vs local timezone mismatch
              const occurrenceDateStr = dayjs(occurrenceDate).format("YYYY-MM-DD");

              const isCancelled = event.extendedProps?.exceptions?.some(
                (ex) => ex.date === occurrenceDateStr && ex.type === "CANCELLED"
              );

              const endTime = new Date(occurrenceDate.getTime() + durationMs);

              expanded.push({
                ...event,
                id: `${event.id || "recurring"}-${occurrenceDate.getTime()}`,
                start: occurrenceDate.toISOString(),
                end: endTime.toISOString(),
                isVirtualOccurrence: true,
                extendedProps: {
                  ...event.extendedProps,
                  isCancelled,
                },
                originalEventId: event.id,
                occurrenceIndex: index,
                rrule: event.rrule,
              });
            });
          } catch (error) {
            console.error("Error expanding recurring event:", event.title, error);
            expanded.push(event);
          }
        } else {
          expanded.push(event);
        }
      });

      return expanded;
    },
    [],
  );

  const filterByDate = useCallback(
    (items, dateFilter) => {
      if (!dateFilter) return items;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      switch (dateFilter) {
        case "upcoming":
          return items.filter((item) => new Date(item.start) >= today);
        case "today":
          return items.filter((item) => {
            const eventDate = new Date(item.start);
            return eventDate >= today && eventDate < tomorrow;
          });
        case "thisWeek": {
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 7);
          return items.filter((item) => {
            const eventDate = new Date(item.start);
            return eventDate >= weekStart && eventDate < weekEnd;
          });
        }
        case "thisMonth": {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          return items.filter((item) => {
            const eventDate = new Date(item.start);
            return eventDate >= monthStart && eventDate < monthEnd;
          });
        }
        case "past":
          return items.filter((item) => new Date(item.start) < today);
        case "dateRange": {
          if (fromDate && toDate) {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            return items.filter((item) => {
              const eventDate = new Date(item.start);
              return eventDate >= from && eventDate <= to;
            });
          }
          return items;
        }
        default:
          return items;
      }
    },
    [fromDate, toDate],
  );

  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;
    const searchLower = term.toLowerCase().trim();
    return items.filter((item) =>
      (item.title || "").toLowerCase().includes(searchLower)
    );
  }, []);

  const filteredData = useMemo(() => {
    const expandedEvents = expandRecurringEvents(eventData);
    let result = [...expandedEvents];

    // Remove cancelled occurrences entirely from the calendar view
    result = result.filter((item) => !item.extendedProps?.isCancelled);

    result = filterByDate(result, filters.eventDate);

    if (filters.eventType) {
      if (filters.eventType === "recurring") {
        result = result.filter(
          (item) =>
            !!(item.extendedProps?.isRecurring || item.rrule || item.isVirtualOccurrence)
        );
      } else if (filters.eventType === "nonrecurring") {
        result = result.filter(
          (item) =>
            !(item.extendedProps?.isRecurring || item.rrule || item.isVirtualOccurrence)
        );
      }
    }

    if (filters.venue) {
      result = result.filter((item) => {
        const venue = item.extendedProps?.venue || "";
        if (filters.venue === "online") {
          return venue.toLowerCase().includes("online");
        } else if (filters.venue === "offline") {
          return !venue.toLowerCase().includes("online");
        }
        return true;
      });
    }

    result = globalSearch(result, searchTerm);

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    if (!filters.eventDate) {
      result = result.filter((item) => {
        const eventDate = new Date(item.start);
        const eventDateOnly = new Date(
          eventDate.getFullYear(),
          eventDate.getMonth(),
          eventDate.getDate(),
        );

        if (eventDateOnly > today) return true;

        if (eventDateOnly.getTime() === today.getTime()) {
          const eventHour = eventDate.getHours();
          const eventMinute = eventDate.getMinutes();
          if (eventHour > currentHour) return true;
          if (eventHour === currentHour && eventMinute >= currentMinute) return true;
          return false;
        }

        return false;
      });
    }

    result.sort((a, b) => {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);

      if (filters.eventDate === "past") {
        const dateDiff = dateB - dateA;
        if (dateDiff !== 0) return dateDiff;
        return dateB.getTime() - dateA.getTime();
      }

      const dateDiff = dateA - dateB;
      if (dateDiff !== 0) return dateDiff;
      return dateA.getTime() - dateB.getTime();
    });

    return result;
  }, [eventData, filters, searchTerm, expandRecurringEvents, filterByDate, globalSearch]);

  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  const handleSearch = useCallback((term) => {
    dispatch({ type: "setSearchTerm", payload: term });
  }, []);

  const handleFilterChange = useCallback(
    (newFilters) => {
      if (
        newFilters.eventDate === "dateRange" &&
        filters.eventDate !== "dateRange"
      ) {
        dispatch({ type: "setDateRangeModal", payload: true });
        return;
      }

      if (
        filters.eventDate === "dateRange" &&
        newFilters.eventDate !== "dateRange"
      ) {
        dispatch({ type: "setDateRange", payload: { from: null, to: null } });
        dispatch({ type: "setTempDateRange", payload: { from: null, to: null } });
      }

      dispatch({ type: "setFilters", payload: newFilters });
    },
    [filters],
  );

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  const handleDateRangeChange = useCallback((dates, dateStrings) => {
    dispatch({
      type: "setTempDateRange",
      payload: {
        from: dateStrings[0] || null,
        to: dateStrings[1] || null,
      },
    });
  }, []);

  const applyDateRangeFilter = useCallback(() => {
    if (tempFromDate && tempToDate) {
      dispatch({ type: "setDateRange", payload: { from: tempFromDate, to: tempToDate } });
      dispatch({ type: "setFilters", payload: { ...filters, eventDate: "dateRange" } });
      dispatch({ type: "setDateRangeModal", payload: false });
    } else {
      showNotification("Please select both from and to dates", "warning");
    }
  }, [tempFromDate, tempToDate, filters, showNotification]);

  const handleDateRangeModalCancel = useCallback(() => {
    dispatch({ type: "setDateRangeModal", payload: false });
    dispatch({ type: "setTempDateRange", payload: { from: null, to: null } });
    dispatch({ type: "setFilters", payload: {} });
  }, []);

  const showPopup = useCallback((eventInfo) => {
    const calendarTop = document.querySelector(".fc").getBoundingClientRect().top;
    const rect = eventInfo.el.getBoundingClientRect();
    const spacing = 10;
    const popupWidth = 300;
    const isFirstRow = rect.top - calendarTop < rect.height + 100;
    const isSecondRow = rect.top - calendarTop < rect.height + 2 * 100;
    const isNearRightEdge = rect.left + popupWidth > window.innerWidth;

    setPopupInfo({
      event: eventInfo.event,
      top: isFirstRow || isSecondRow
        ? rect.bottom + window.scrollY + spacing
        : rect.top + window.scrollY - spacing,
      left: isNearRightEdge
        ? rect.right + window.scrollX - popupWidth + 75
        : rect.left + window.scrollX,
      alignLeft: isNearRightEdge,
      above: !(isFirstRow || isSecondRow),
    });
    setPopupVisible(true);
  }, []);

  const hidePopup = useCallback(() => {
    if (isHoveringPopupRef.current) return;
    setPopupVisible(false);
    setPopupInfo(null);
  }, []);

  const prepareExcelData = useCallback(() => {
    return filteredData.map((event) => ({
      "Event Title": event.title,
      Date: event.start ? new Date(event.start).toLocaleDateString() : "N/A",
      Time: event.start ? new Date(event.start).toLocaleTimeString() : "N/A",
      Location: event.extendedProps?.location || "N/A",
      Type:
        event.extendedProps?.isRecurring || event.rrule || event.isVirtualOccurrence
          ? "Recurring"
          : "One-time",
      Venue: event.extendedProps?.venue || "N/A",
      Description: event.extendedProps?.description || "N/A",
    }));
  }, [filteredData]);

  const formatEventTime = (event) => {
    if (event.isVirtualOccurrence || event.start) {
      const start = new Date(event.start);
      const end = event.end ? new Date(event.end) : null;
      const timeFormat = { hour: "2-digit", minute: "2-digit", hour12: true };

      if (end) {
        return `${start.toLocaleTimeString("en-US", timeFormat)} - ${end.toLocaleTimeString("en-US", timeFormat)}`;
      }
      return start.toLocaleTimeString("en-US", timeFormat);
    }

    if (event.rrule && event.rrule.dtstart) {
      const start = new Date(event.rrule.dtstart);
      const timeFormat = { hour: "2-digit", minute: "2-digit", hour12: true };

      if (event.duration) {
        const durationMs =
          event.duration === "02:00:00" ? 2 * 60 * 60 * 1000 : 60 * 60 * 1000;
        const end = new Date(start.getTime() + durationMs);
        return `${start.toLocaleTimeString("en-US", timeFormat)} - ${end.toLocaleTimeString("en-US", timeFormat)}`;
      }
      return start.toLocaleTimeString("en-US", timeFormat);
    }

    return "Time TBD";
  };

  const formatEventDate = (event) => {
    if (event.isVirtualOccurrence || event.start) {
      const date = new Date(event.start);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }

    if (event.rrule && event.rrule.dtstart) {
      const date = new Date(event.rrule.dtstart);
      const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
      return `Every ${dayName}`;
    }

    return "Date TBD";
  };

  // FIX: Extract fetchEvents so it can be called both on mount and after delete/update
  const fetchEvents = useCallback(async () => {
    dispatch({ type: "setLoading", payload: true });
    try {
      const token = await getAuthToken();
      const res = await axios.get(API_ENDPOINTS.CALENDAR, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("API response:", res);

      const eventsData = res.data?.events;

      if (Array.isArray(eventsData)) {
        const transformedEvents = eventsData.map((event, index) => {
          const [startTimeStr, endTimeStr] = event.timings.split(" to ");

          const formatTime = (timeStr) => {
            const [time, modifier] = timeStr.trim().split(" ");
            let [hours, minutes] = time.split(":").map(Number);
            if (modifier.toLowerCase() === "pm" && hours !== 12) hours += 12;
            if (modifier.toLowerCase() === "am" && hours === 12) hours = 0;
            return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;
          };

          const startTime = formatTime(startTimeStr);
          const endTime = formatTime(endTimeStr);
          const date = event.date.replace(/\//g, "-");

          const baseEvent = {
            id: event.id || `event-${index}`,
            title: event.title || "Untitled Event",
            extendedProps: {
              ...event,
              id: event.id,
              isRecurring: event.recurring || event.isRecurring || false,
              exceptions: event.exceptions || [],
            },
          };

          // FIX: Only use recurring/isRecurring to decide rrule path.
          // Do NOT use hasExceptions here — a single event can have exceptions
          // on the backend (e.g. test3) and should NOT be treated as recurring.
          if (event.recurring || event.isRecurring) {
            return {
              ...baseEvent,
              rrule: {
                freq: "weekly",
                dtstart: `${date}T${startTime}`,
              },
              duration: "02:00:00",
            };
          } else {
            return {
              ...baseEvent,
              start: `${date}T${startTime}`,
              end: `${date}T${endTime}`,
            };
          }
        });

        setEvents(transformedEvents);
        dispatch({ type: "setEventData", payload: transformedEvents });
      } else {
        console.error("No event data array found.");
        setEvents([]);
        dispatch({ type: "setEventData", payload: [] });
      }
    } catch (err) {
      console.error("API error:", err);
      showNotification("Failed to load events: " + err.message, "error");
      setEvents([]);
      dispatch({ type: "setEventData", payload: [] });
    }
    dispatch({ type: "setLoading", payload: false });
  }, [showNotification]);

  // Initial fetch on mount
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // FIX: Refetch whenever eventDetails navigates back with { state: { refetch: true } }
  // This ensures deleted/updated events are reflected immediately without a manual refresh
  useEffect(() => {
    if (location.state?.refetch) {
      fetchEvents();
      // Clear the state so a back-navigation later doesn't re-trigger the fetch
      window.history.replaceState({}, "");
    }
  }, [location.state, fetchEvents]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setPopupVisible(false);
        setPopupInfo(null);
      }
    };

    if (popupInfo && popupVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupInfo, popupVisible]);

  return (
    <Layout
      navTitle="Calendar & Events"
      notificationProps={{
        show: notification.show,
        message: notification.message,
        type: notification.type,
        onClose: hideNotification,
      }}
    >
      <div className="event-btn-set">
        <Button onClick={openDrawer} className="event-search-btn">
          <FaSearch />
          <span>Search for events</span>
        </Button>
        <Button
          onClick={() => navigate("/eventDetails")}
          className="create-event-btn"
        >
          Create New Event
        </Button>
      </div>

      <Drawer
        title="Events List & Search"
        onClose={closeDrawer}
        maskClosable={true}
        open={open}
        className="event-search-panel"
        width={750}
      >
        <TopToolBar
          filtersConfig={filtersConfig}
          searchPlaceholder="Search events by title..."
          searchTerm={searchTerm}
          filters={filters}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          onReset={handleReset}
          showExcelDownload={true}
          excelData={prepareExcelData()}
          excelFilename="events-list"
          excelSheetName="Events"
        />

        <div style={{ marginTop: "16px" }}>
          <List
            loading={isLoading}
            itemLayout="vertical"
            split={true}
            bordered={true}
            className="event-search-list"
            dataSource={currentPageData}
            locale={{
              emptyText:
                searchTerm || Object.keys(filters).length > 0
                  ? "No events match your search criteria"
                  : "No events available",
            }}
            renderItem={(event) => (
              <List.Item
                key={event.id}
                actions={[
                  <Button
                    type="link"
                    icon={<FaArrowCircleRight />}
                    onClick={() => {
                      const eventData = {
                        title: event.title,
                        start: event.start,
                        end: event.end,
                        extendedProps: {
                          ...event.extendedProps,
                          id: event.extendedProps?.id || event.id,
                        },
                      };
                      navigate("/eventDetails", { state: eventData });
                      closeDrawer();
                    }}
                  >
                    View Details
                  </Button>,
                ]}
                extra={
                  <div className="event-type-badge">
                    {event.extendedProps?.isRecurring ||
                    event.rrule ||
                    event.isVirtualOccurrence ? (
                      <span className="recurring-badge">Recurring</span>
                    ) : (
                      <span className="onetime-badge">One-time</span>
                    )}
                  </div>
                }
              >
                <List.Item.Meta
                  title={
                    <div className="event-title">
                      <FaCalendarAlt style={{ marginRight: "8px", color: "#1890ff" }} />
                      {event.title}
                    </div>
                  }
                  description={
                    <div className="event-details">
                      <div className="event-datetime">
                        <FaClock style={{ marginRight: "6px", color: "#52c41a" }} />
                        {formatEventDate(event)} • {formatEventTime(event)}
                      </div>
                      {event.extendedProps?.location && (
                        <div className="event-location">
                          <FaMapMarkerAlt style={{ marginRight: "6px", color: "#fa8c16" }} />
                          {event.extendedProps.location}
                        </div>
                      )}
                      {event.extendedProps?.description && (
                        <div className="event-description">
                          {event.extendedProps.description}
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />

          {filteredData.length > pageSize && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                onChange={handlePaginationChange}
                showSizeChanger={false}
                showQuickJumper={false}
                size="small"
              />
            </div>
          )}
        </div>
      </Drawer>

      <div className="event-wrapper" ref={calendarWrapperRef}>
        <div className="calendar-view">
          <FullCalendar
            plugins={[
              timeGridPlugin,
              dayGridPlugin,
              interactionPlugin,
            ]}
            datesSet={(arg) => {
              setCalendarRange({ start: arg.start, end: arg.end });
            }}
            initialView="dayGridMonth"
            weekends={true}
            fixedWeekCount={false}
            events={filteredData}
            height="100%"
            headerToolbar={{
              start: "today prev next",
              center: "title",
              end: "dayGridWeek dayGridMonth",
            }}
            displayEventTime={false}
            eventContent={(arg) => {
              const isCancelled = arg.event.extendedProps?.isCancelled;
              return (
                <div className="fc-event-title">
                  {arg.event.title}
                  {isCancelled && (
                    <div style={{ fontSize: "10px", color: "red" }}>
                      Cancelled
                    </div>
                  )}
                </div>
              );
            }}
            eventClassNames={(arg) => {
              if (arg.event.extendedProps?.isCancelled) {
                return ["cancelled-event"];
              }
              return arg.event.extendedProps?.isRecurring || arg.event.rrule
                ? ["recurring-event"]
                : ["nonrecurring-event"];
            }}
            eventMouseEnter={(info) => showPopup(info)}
            eventMouseLeave={() => hidePopup()}
          />
        </div>

        {popupInfo && (
          <div
            className="evl-popup-container"
            ref={popupRef}
            onMouseEnter={() => {
              isHoveringPopupRef.current = true;
            }}
            onMouseLeave={() => {
              isHoveringPopupRef.current = false;
              hidePopup();
            }}
            style={{
              position: "absolute",
              left: popupInfo.left,
              top: popupInfo.above ? undefined : popupInfo.top - 10,
              bottom: popupInfo.above
                ? window.innerHeight - popupInfo.top - 10
                : undefined,
              opacity: popupVisible ? 1 : 0,
              transition: "opacity 0.1s ease-in-out",
              pointerEvents: popupVisible ? "auto" : "none",
            }}
          >
            <h3>{popupInfo.event.title}</h3>
            <p>
              <strong>Location:</strong>{" "}
              {popupInfo.event.extendedProps?.location}
            </p>
            <p>
              <strong>Start:</strong>{" "}
              {popupInfo.event.start?.toLocaleString()}
            </p>
            <p>
              <strong>Description:</strong>{" "}
              {popupInfo.event.extendedProps?.description}
            </p>
            <button
              className="evl-eventdetails-btn"
              onClick={() => {
                const { title, start, end, extendedProps } = popupInfo.event;
                const eventData = {
                  title,
                  start: start?.toISOString(),
                  end: end?.toISOString(),
                  extendedProps: {
                    ...extendedProps,
                    id: extendedProps?.id || popupInfo.event.id,
                  },
                };
                navigate("/eventDetails", { state: eventData });
              }}
            >
              <FaArrowCircleRight id="evl-arrow-icon" /> Event Details
            </button>
          </div>
        )}
      </div>

      <Modal
        title="Select Date Range"
        open={showDateRangeModal}
        onCancel={handleDateRangeModalCancel}
        footer={[
          <Button key="cancel" onClick={handleDateRangeModalCancel}>
            Cancel
          </Button>,
          <Button key="apply" onClick={applyDateRangeFilter}>
            Apply Filter
          </Button>,
        ]}
        width={450}
        centered={false}
        style={{ top: 120 }}
      >
        <div style={{ marginBottom: "20px" }}>
          <RangePicker
            style={{ width: "100%" }}
            value={
              tempFromDate && tempToDate
                ? [dayjs(tempFromDate), dayjs(tempToDate)]
                : []
            }
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            placeholder={["From Date", "To Date"]}
          />
        </div>
      </Modal>
    </Layout>
  );
}

export default EventList;